import { gql } from '@apollo/client'

const MANAGE_PROJECTS_MODEL = `
    id
    title
    owners {
        id
        email
        firstName
        lastName
        fullName
    }
    start_date
    end_date
    status
    team {
        id
        name
    }
`

const MANAGE_JOBS_MODEL = `
    id
    user {
        id
        fullName
        firstName
        lastName
        email
    }
    expert {
        stripeAccount {
            isConnected
        }
    }
    project {
        id
        type
        title
        team {
            id
            name
        }
    }
    response_status
    overall_status
    invitedReminderAt
    meetingAt
    updatedAt
`

const MANAGE_ORGANIZATIONS_MODEL = `
    id
    name
    type
    status
    url
    size
    description
    location {
        city
        country
    }
`

const AUTH_USER_RECORD_MODEL = `
    user {
        id
        email
        firstName
        lastName
        fullName
        referredByPartner
        expert {
            id
            referredByPartner
        }
    }
    signedUpAt
    emailVerified
    emailVerifiedAt
    passwordConfigured
    passwordUpdatedAt
    failedLoginAttempts
    lockedUntil
    isBlocked
    blockedAt
    blockedReason
    blockedBy {
        id
        email
        fullName
    }
    lastLoginAt
    recentTokens {
        id
        type
        createdAt
        expiresAt
        usedAt
        revokedAt
        replaced
        userAgent
        ip
    }
`

export const LOAD_MANAGE_PROJECTS_QUERY = gql`
    query AdminPortal {
        adminPortal {
            actionableProjects {
                ${MANAGE_PROJECTS_MODEL}
            }
        }
    }
`

export const LOAD_MANAGE_JOBS_QUERY = gql`
    query AdminPortal {
        adminPortal {
            actionableJobs {
                ${MANAGE_JOBS_MODEL}
            }
        }
    }
`

export const LOAD_MANAGE_ORGANIZATIONS_QUERY = gql`
    query AdminPortal {
        adminPortal {
            actionableOrganizations {
                ${MANAGE_ORGANIZATIONS_MODEL}
            }
        }
    }
`

export const LOAD_AGENDA_STATS_QUERY = gql`
    query AdminPortal {
        adminPortal {
            agendaStats {
                name
                failed
                lastFailedAt
                succeeded
                lastSucceededAt
                total
                queued
                avgDurationMs
            }
        }
    }
`

export const LOAD_EXPERT_RECOMMENDATION_PERFORMANCE_QUERY = gql`
    query AdminPortal {
        adminPortal {
            expertRecommendationPerformance {
                generatedAt
                categories {
                    category
                    total
                    invitedCount
                    acceptedCount
                    acceptanceRate
                    screeningEligibleCount
                    screeningPassedCount
                    screeningPassRate
                    projects {
                        projectId
                        projectTitle
                        updatedAt
                        total
                        invitedCount
                        acceptedCount
                        acceptanceRate
                        screeningEligibleCount
                        screeningPassedCount
                        screeningPassRate
                    }
                }
            }
        }
    }
`
export const LOAD_AI_USAGE_COST_SUMMARY_QUERY = gql`
    query AdminPortalAIUsageCostSummary($month: String) {
        adminPortal {
            aiUsageCostSummary(month: $month) {
                month
                availableMonths
                teams {
                    teamId
                    teamName
                    promptTokens
                    candidatesTokens
                    thoughtsTokens
                    cost
                }
                platformTotal {
                    promptTokens
                    candidatesTokens
                    thoughtsTokens
                    cost
                }
            }
        }
    }
`
export const LOAD_AI_USAGE_COST_TEAM_PROJECTS_QUERY = gql`
    query AdminPortalAIUsageCostTeamProjects(
        $month: String
        $teamId: String!
        $day: String
    ) {
        adminPortal {
            aiUsageCostTeamProjects(month: $month, teamId: $teamId, day: $day) {
                projectId
                projectTitle
                promptTokens
                candidatesTokens
                thoughtsTokens
                cost
            }
        }
    }
`
export const LOAD_AI_USAGE_COST_PROJECT_RECORDS_QUERY = gql`
    query AdminPortalAIUsageCostProjectRecords(
        $month: String
        $teamId: String!
        $projectId: String
        $day: String
    ) {
        adminPortal {
            aiUsageCostProjectRecords(
                month: $month
                teamId: $teamId
                projectId: $projectId
                day: $day
            ) {
                id
                name
                userName
                promptTokens
                candidatesTokens
                thoughtsTokens
                cost
                createdAt
            }
        }
    }
`
export const LOAD_AI_USAGE_COST_DAILY_QUERY = gql`
    query AdminPortalAIUsageCostDaily($month: String) {
        adminPortal {
            aiUsageCostDaily(month: $month) {
                date
                promptTokens
                candidatesTokens
                thoughtsTokens
                cost
            }
        }
    }
`
export const LOAD_AI_USAGE_COST_DAY_TEAMS_QUERY = gql`
    query AdminPortalAIUsageCostDayTeams($day: String!) {
        adminPortal {
            aiUsageCostDayTeams(day: $day) {
                day
                teams {
                    teamId
                    teamName
                    promptTokens
                    candidatesTokens
                    thoughtsTokens
                    cost
                }
                dayTotal {
                    promptTokens
                    candidatesTokens
                    thoughtsTokens
                    cost
                }
            }
        }
    }
`
export const LOAD_AUTH_USERS_QUERY = gql`
    query AuthUsers($query: String, $pagination: Pagination) {
        authUsers(query: $query, pagination: $pagination) {
            total
            users {
                ${AUTH_USER_RECORD_MODEL}
            }
        }
    }
`

export const CREATE_USER = gql`
    mutation CreateUser($input: CreateUserInput!) {
        createUser(input: $input) {
            id
            email
            firstName
            lastName
            fullName
        }
    }
`

export const UPDATE_USER_AUTH = gql`
    mutation UpdateUserAuth($userId: ID!, $input: UpdateUserAuthInput!) {
        updateUserAuth(userId: $userId, input: $input) {
            id
            email
            firstName
            lastName
            fullName
        }
    }
`

export const SEND_AUTH_EMAIL_TO_USER = gql`
    mutation SendAuthEmailToUser(
        $userId: ID!
        $type: String!
        $targetApp: String!
    ) {
        sendAuthEmailToUser(userId: $userId, type: $type, targetApp: $targetApp)
    }
`

export const SWITCH_TO_USER = gql`
    mutation SwitchToUser($email: String!) {
        switchToUser(email: $email) {
            id
        }
    }
`

export const MERGE_USER = gql`
    mutation MergeUser(
        $sourceUserEmail: String!
        $targetUserEmail: String!
        $deleteSourceUser: Boolean
    ) {
        mergeUser(
            sourceUserEmail: $sourceUserEmail
            targetUserEmail: $targetUserEmail
            deleteSourceUser: $deleteSourceUser
        ) {
            id
        }
    }
`

export const LOGOUT = gql`
    mutation Logout($id: ID!) {
        logout(id: $id) {
            id
        }
    }
`

// De novo sourcing Stage 2. One document per external expert — a person from
// the professional registry who is not one of our experts — carrying every page
// the web search found and whether each one is about them. The registry facts
// are copied onto the document rather than joined, so callers need no separate
// registry lookup.
const EXTERNAL_EXPERT_MODEL = `
    professionalRegistrations {
        type
        number
    }
    firstName
    lastName
    fullName
    credentials
    specialties
    organizations
    city
    region
    foundStatus
    attemptCount
    lastAttemptedAt
    registryDirectoryCount
    searches {
        kind
        query
        resultCount
        unresponsiveEngines
    }
    webSources {
        url
        host
        kind
        foundBy
        pageTitle
        snippet
        pageText
        matchStatus
        matchReason
        echoed
        links
    }
    judgedBy {
        model
        promptVersion
    }
`

// The list asks for no page text at all. A person carries around thirty pages
// with their snippets, so a hundred full documents is a couple of megabytes —
// far more than a table of names needs.
export const LOAD_EXTERNAL_EXPERTS_QUERY = gql`
    query ExternalExperts {
        adminPortal {
            externalExperts {
                professionalRegistrations {
                    type
                    number
                }
                fullName
                foundStatus
                lastAttemptedAt
                specialties
                organizations
                city
                region
                webSources {
                    matchStatus
                }
            }
        }
    }
`

export const LOAD_EXTERNAL_EXPERT_QUERY = gql`
    query ExternalExpert($registrationNumber: String!) {
        adminPortal {
            externalExpert(registrationNumber: $registrationNumber) {
                ${EXTERNAL_EXPERT_MODEL}
            }
        }
    }
`
